import {useEffect, useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {Screen, Text} from '#component/base';
import {LoadingSpinner} from '#component/base/LoadingSpinner';
import {Hero, HorizontalProgressTracker} from '#component/partial';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {SURVEY_ONBOARDING_GIFTED_KEY, SURVEY_ONBOARDING_KEY} from '#constant/survey';
import {sprinkles} from '#style/sprinkles';

import {Questions} from './components/Questions/Questions';
import {QuestionResponse} from './SurveyOnboarding.type';
import {useSurveyOnboarding} from './useSurveyOnboarding';

import * as css from './SurveyOnboarding.css';

const SurveyOnboardingScreen: NextPage = () => {
  const {query, push} = useRouter();
  const survey_key = query.survey as string | undefined;
  const isGiftedTestKit = survey_key === SURVEY_ONBOARDING_GIFTED_KEY;

  const [questionSelected, setQuestionSelected] = useState<string | null>(null);
  const [questionResponse, setQuestionResponse] = useState<QuestionResponse[]>([]);
  const {data, handleSave} = useSurveyOnboarding(
    isGiftedTestKit ? SURVEY_ONBOARDING_GIFTED_KEY : SURVEY_ONBOARDING_KEY,
  );
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const questionIndex = data?.questions.findIndex(item => item.id === questionSelected) as number;

  const progressTrackerSteps = [
    {
      title: 'Order Confirmed',
    },
    {
      title: 'Create Account',
    },
    {
      title: 'Account Setup',
    },
  ].slice(isGiftedTestKit ? 1 : 0);

  const onSubmitSuccess = () => {
    const postSurveyPath = isGiftedTestKit ? '/register' : '/dashboard';
    push(postSurveyPath);
  };

  const handleNextQuestion = async () => {
    if (data) {
      const isLastIndex = questionIndex === data.questions.length - 1;

      if (!isLastIndex) {
        setQuestionSelected(data.questions[questionIndex + 1].id);
      } else {
        setIsFormSubmitting(true);
        window.scrollTo({
          top: 150,
          behavior: 'smooth',
        });
        await handleSave(questionResponse, {onSuccess: onSubmitSuccess});
      }
    }
  };

  const handleBackQuestion = () => {
    if (data) {
      setQuestionSelected(data.questions[questionIndex - 1].id);
    }
  };

  const handleEditQuestion = (value: string[], index: number, isMultiSelect: boolean) => {
    const deselectOptions = ['option-112', 'option-76'];
    if (isMultiSelect) {
      setQuestionResponse(prevState => {
        return prevState.map((item: QuestionResponse, i: number) => {
          if (i === index) {
            // Check if any value selected is in the deselectOptions array
            // option-112 represents "Prefer not to answer"
            // option-76 represents "None of the above"
            // If the user selects either after other options were selected, all other options will be
            // deselected. If the user selects other options after selecting either, option-112/ option-76
            // will be deselected.
            if (
              value.some(val => deselectOptions.includes(val)) &&
              !value.some(val => item.answerOptionIds.includes(val))
            ) {
              return {...item, answerOptionIds: value};
            }
            // Toggle the selection of the options
            const updatedArray = item.answerOptionIds.includes(value[0])
              ? item.answerOptionIds.filter(existingValue => existingValue !== value[0])
              : [...item.answerOptionIds, value[0]];
            const newArray = updatedArray.filter(
              existingValue => !deselectOptions.includes(existingValue),
            );
            // Check if any value from deselectOptions is already in the array of values
            const isDeselectOptionSelected = newArray.some(val => deselectOptions.includes(val));
            // Remove values from deselectOptions if another option is selected
            const finalArray = isDeselectOptionSelected
              ? newArray.filter(existingValue => !deselectOptions.includes(existingValue))
              : newArray;
            return {...item, answerOptionIds: finalArray};
          }
          return item;
        });
      });
    } else {
      setQuestionResponse(prevState => {
        return prevState.map((item: QuestionResponse, i: number) =>
          i === index ? {...item, answerOptionIds: value} : item,
        );
      });
    }
  };

  useEffect(() => {
    if (data) {
      setQuestionSelected(data.questions[0].id);
      const response = data.questions.map(item => {
        return {
          questionId: item.id,
          questionType: item.questionType,
          answerOptionIds: [],
        };
      });
      setQuestionResponse(response);
    }
  }, [data]);

  useEffect(() => {
    window.scrollTo({
      top: 150,
      behavior: 'smooth',
    });
  }, [questionSelected]);

  return (
    <Screen
      pageName="Survey Onboarding"
      withDefaultSizing={false}
      contentContainerClassName={sprinkles({
        flex: 'flex',
        dir: 'col',
        items: 'center',
      })}>
      <Hero variant="sunrise" showOverlap={false}>
        <div className={css.hero}>
          <Text
            className={sprinkles({w: 'full'})}
            color="deepBurgundy"
            variant="heading1"
            textAlign="center">
            Let&apos;s get started
          </Text>
          <Text className={sprinkles({w: 'full', pX: 24})} color="doveGray" textAlign="center">
            Your account has been created! Let’s answer a few questions to begin your journey
          </Text>
          <div className={sprinkles({w: 'full', pX: 24})}>
            <HorizontalProgressTracker
              items={progressTrackerSteps}
              currentItemIndex={progressTrackerSteps.length - 1}
              currentItemStatus="in-progress"
            />
          </div>
        </div>
      </Hero>
      {data && questionSelected && !isFormSubmitting ? (
        <Questions
          questions={data.questions || []}
          questionSelected={questionSelected}
          valueQuestions={questionResponse}
          nextQuestion={handleNextQuestion}
          backQuestion={handleBackQuestion}
          editQuestion={handleEditQuestion}
        />
      ) : (
        <div className={sprinkles({mT: 32})}>
          <LoadingSpinner size="large" />
        </div>
      )}
    </Screen>
  );
};

export const SurveyOnboarding: NextPage = withRequireUserState(SurveyOnboardingScreen, {
  userState: 'authenticated',
});
